/**
 * The server's Notification settings (2026-10-29), as Discord's dialog: mute
 * the server (with how long), the server level (All messages, Only
 * @mentions, Nothing), suppress @everyone / @here and role mentions, and
 * the overrides, a level and a mute per category or channel. Every change
 * applies at once (and to every device); there is nothing to save. A glass
 * dialog on the desktop, a sheet on a phone (the kit's Dialog).
 */
import {
  ChannelType,
  compareSnowflakes,
  guildLevel,
  NotificationLevel,
  overrideOf,
  Permissions,
  type Channel,
  type ChannelNotificationOverride,
  type MuteDuration,
  type NotificationLevel as Level,
  type Snowflake,
} from '@neosocial/protocol';
import { useState } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { useFlag } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { can, channelPermissions, useChannelTree } from '../store/selectors';
import { Btn, Icon, IconBtn, Seg, Select, Switch } from '../ui/kit';
import { Dialog } from '../ui/overlay';
import { SetGroup, SetRow } from '../settings/Settings';
import {
  dropOverride,
  muteChannel,
  muteGuild,
  patchGuildNotify,
  setChannelLevel,
  setGuildLevel,
  unmuteChannel,
  unmuteGuild,
} from './actions';
import { DURATIONS, durationLabel, levelName, mutedUntil } from './labels';
import { isGuildMuted, isOwnMute, serverDefault, settingsIn } from './rules';

export const useNotifyDialog = create<{ guildId: Snowflake | null }>(() => ({ guildId: null }));

export function openNotifyDialog(guildId: Snowflake): void {
  useNotifyDialog.setState({ guildId });
}

function close(): void {
  useNotifyDialog.setState({ guildId: null });
}

/** Mounted once in the shell: the dialog while it is open (and the flag on). */
export function NotifyDialogHost() {
  const guildId = useNotifyDialog((s) => s.guildId);
  const on = useFlag('notifications');
  if (!guildId || !on) return null;
  return <NotifyDialog guildId={guildId} />;
}

function NotifyDialog({ guildId }: { guildId: Snowflake }) {
  const { t } = useT();
  const name = useData((d) => d.guilds[guildId]?.name ?? '');
  const settings = useData((d) => settingsIn(d, guildId));
  const def = useData((d) => serverDefault(d, guildId));
  // read in the store's selector: wire.ts redraws when a timed mute runs out
  const muted = useData((d) => isGuildMuted(d, guildId));
  // the length the next mute takes: the running one's, else until turned back on
  const [window, setWindow] = useState<MuteDuration>(
    settings?.mute_config?.selected_time_window ?? -1,
  );
  const own = settings?.message_notifications ?? NotificationLevel.INHERIT;
  const level = guildLevel(settings, def);
  return (
    <Dialog
      title={t('nt.settings')}
      icon="bell"
      size="md"
      cls="nt-dlg"
      onClose={close}
      actions={
        <Btn kind="primary" size="sm" onClick={close}>
          {t('nt.dlg.done')}
        </Btn>
      }
    >
      <SetGroup>
        <SetRow
          label={t('nt.dlg.muteRow', { name })}
          sub={muted ? mutedUntil(settings?.mute_config) : t('nt.dlg.muteSub')}
        >
          <Select
            className="nx-input ns-select nt-dur"
            aria-label={t('nt.dlg.muteFor')}
            value={String(window)}
            onChange={(e) => {
              const w = Number(e.target.value) as MuteDuration;
              setWindow(w);
              // a running mute takes the new length from now
              if (muted) void muteGuild(guildId, w);
            }}
          >
            {DURATIONS.map((w) => (
              <option key={w} value={String(w)}>
                {durationLabel(w)}
              </option>
            ))}
          </Select>
          <Switch
            checked={muted}
            label={t('nt.dlg.muteRow', { name })}
            onChange={(v) => void (v ? muteGuild(guildId, window) : unmuteGuild(guildId))}
          />
        </SetRow>
      </SetGroup>
      <SetGroup title={t('nt.dlg.level')}>
        <SetRow
          label={t('nt.dlg.notifyAbout')}
          sub={
            own === NotificationLevel.INHERIT
              ? t('nt.dlg.levelSub', { level: levelName(level) })
              : undefined
          }
          stack
        >
          <Seg<string>
            label={t('nt.dlg.level')}
            // three long labels: a list on a phone, as Discord's (QA D3: "All messa…")
            phoneList
            value={String(level)}
            onChange={(v) => void setGuildLevel(guildId, Number(v) as Level)}
            options={[
              { value: '0', label: t('nt.all') },
              { value: '1', label: t('nt.mentions') },
              { value: '2', label: t('nt.nothing') },
            ]}
          />
        </SetRow>
        <SetRow label={t('nt.dlg.suppressEveryone')} sub={t('nt.dlg.suppressEveryoneSub')}>
          <Switch
            checked={!!settings?.suppress_everyone}
            label={t('nt.dlg.suppressEveryone')}
            onChange={(v) => void patchGuildNotify(guildId, { suppress_everyone: v })}
          />
        </SetRow>
        <SetRow label={t('nt.dlg.suppressRoles')} sub={t('nt.dlg.suppressRolesSub')}>
          <Switch
            checked={!!settings?.suppress_roles}
            label={t('nt.dlg.suppressRoles')}
            onChange={(v) => void patchGuildNotify(guildId, { suppress_roles: v })}
          />
        </SetRow>
      </SetGroup>
      <Overrides guildId={guildId} />
    </Dialog>
  );
}

/** The categories and channels you can see, in sidebar order (categories first in each group). */
function useNotifiable(guildId: Snowflake): Channel[] {
  const tree = useChannelTree(guildId);
  const visible = useDataShallow((d) =>
    Object.values(d.channels)
      .filter(
        (c) => c.guild_id === guildId && can(channelPermissions(d, c), Permissions.VIEW_CHANNEL),
      )
      .map((c) => c.id),
  );
  const out: Channel[] = [];
  for (const n of tree) {
    if (n.category && visible.includes(n.category.id)) out.push(n.category);
    for (const c of n.channels) if (visible.includes(c.id)) out.push(c);
  }
  return out;
}

function Overrides({ guildId }: { guildId: Snowflake }) {
  const { t } = useT();
  const settings = useData((d) => settingsIn(d, guildId));
  const channels = useNotifiable(guildId);
  // rows added here but not yet changed stay listed until the dialog closes
  const [added, setAdded] = useState<Snowflake[]>([]);
  const overridden = new Set((settings?.channel_overrides ?? []).map((o) => o.channel_id));
  const rows = channels.filter((c) => overridden.has(c.id) || added.includes(c.id));
  const left = channels.filter((c) => !overridden.has(c.id) && !added.includes(c.id));
  return (
    <SetGroup title={t('nt.dlg.overrides')}>
      <SetRow label={t('nt.dlg.add')} sub={t('nt.dlg.overridesSub')}>
        <Select
          className="nx-input ns-select"
          aria-label={t('nt.dlg.add')}
          value=""
          disabled={!left.length}
          onChange={(e) => {
            const id = e.target.value;
            if (id) setAdded((a) => [...a, id]);
          }}
        >
          <option value="">{t('nt.dlg.addPick')}</option>
          {left.map((c) => (
            <option key={c.id} value={c.id}>
              {(c.type === ChannelType.GUILD_CATEGORY ? '' : '# ') + (c.name ?? '')}
            </option>
          ))}
        </Select>
      </SetRow>
      {rows.length ? (
        rows
          .slice()
          .sort(
            (a, b) => channels.indexOf(a) - channels.indexOf(b) || compareSnowflakes(a.id, b.id),
          )
          .map((c) => (
            <OverrideRow
              key={c.id}
              guildId={guildId}
              c={c}
              o={overrideOf(settings, c.id)}
              onRemove={() => {
                setAdded((a) => a.filter((x) => x !== c.id));
                if (overridden.has(c.id)) void dropOverride(guildId, c.id);
              }}
            />
          ))
      ) : (
        <p className="nt-none">{t('nt.dlg.none')}</p>
      )}
    </SetGroup>
  );
}

function OverrideRow({
  guildId,
  c,
  o,
  onRemove,
}: {
  guildId: Snowflake;
  c: Channel;
  o: ChannelNotificationOverride | undefined;
  onRemove: () => void;
}) {
  const { t } = useT();
  const cat = c.type === ChannelType.GUILD_CATEGORY;
  const parent = useData((d) => (c.parent_id ? (d.channels[c.parent_id]?.name ?? '') : ''));
  const muted = useData((d) => isOwnMute(d, c));
  const level = o?.message_notifications ?? NotificationLevel.INHERIT;
  const name = c.name ?? '';
  const sub = muted
    ? mutedUntil(o?.mute_config)
    : cat
      ? t('nt.dlg.category')
      : parent
        ? t('nt.dlg.channelIn', { name: parent })
        : undefined;
  return (
    <div className="nx-set-row nt-ov has-s" data-ch={c.id}>
      <div className="nx-set-rt">
        <div className="l">
          <Icon name={cat ? 'list' : c.type === ChannelType.GUILD_VOICE ? 'volume' : 'hash'} />{' '}
          <bdi>{name}</bdi>
        </div>
        {sub ? <div className="s">{sub}</div> : null}
      </div>
      <div className="nx-set-ctl">
        <Select
          className="nx-input ns-select nt-lvl"
          aria-label={t('nt.dlg.levelOf', { name })}
          value={String(level)}
          onChange={(e) => void setChannelLevel(guildId, c.id, Number(e.target.value) as Level)}
        >
          <option value="3">{t(!cat && c.parent_id ? 'nt.useCategory' : 'nt.useServer')}</option>
          <option value="0">{t('nt.all')}</option>
          <option value="1">{t('nt.mentions')}</option>
          <option value="2">{t('nt.nothing')}</option>
        </Select>
        <Switch
          checked={muted}
          label={t('nt.dlg.muteOf', { name })}
          onChange={(v) => void (v ? muteChannel(guildId, c.id, -1) : unmuteChannel(guildId, c.id))}
        />
        <IconBtn icon="x" label={t('nt.dlg.remove', { name })} onClick={onRemove} />
      </div>
    </div>
  );
}
